import type { ExperimentClient, Variants } from '@amplitude/experiment-js-client';

import { createContext, ReactNode, useState } from 'react';

export type AmplitudeExperimentsContextType = {
    client?: ExperimentClient | undefined;
    deviceId: string | null;
    /**
     * Flag keys pinned via the non-production forceExperiment override.
     * Exposure tracking must NOT fire for these keys (forced devices would pollute experiment data).
     */
    overriddenFlagKeys?: string[];
    setVariants?: (variants: Variants) => void;
    userId: string | null;
    variants: Variants;
};

export type AmplitudeExperimentsContextProps = Omit<AmplitudeExperimentsContextType, 'overriddenFlagKeys'> & {
    children: ReactNode;
    /**
     * Non-production QA override layer merged over the fetched variants at render time —
     * not into the `variants` state — so it survives `setVariants` replacing the whole map
     * (AmplitudeProvider does that on every identity change).
     */
    variantOverrides?: Variants;
};

export const AmplitudeExperimentsContext = createContext<AmplitudeExperimentsContextType>({
    client: null,
    deviceId: null,
    userId: null,
    variants: {},
});

export const AmplitudeExperimentsProvider = ({
    children,
    variantOverrides,
    variants: variantsExternal,
    ...value
}: AmplitudeExperimentsContextProps) => {
    const [variants, setVariants] = useState<Variants>(variantsExternal);

    return (
        <AmplitudeExperimentsContext.Provider
            value={{
                ...value,
                overriddenFlagKeys: variantOverrides ? Object.keys(variantOverrides) : [],
                setVariants,
                variants: { ...variants, ...variantOverrides },
            }}
        >
            {children}
        </AmplitudeExperimentsContext.Provider>
    );
};
